/**
 * S14/S15 kebab entries: share a dashboard as a definition file, capture it
 * as PNG/PDF/interactive HTML, import a definition. Pure triggers — flow
 * state + dialogs live in `useDashboardExportFlow` / `DashboardExportDialogs`
 * (Radix unmounts dropdown content on close, so nothing stateful can live
 * here).
 *
 * "Export as HTML…" is Pro-gated (S15). Enforcement lives in the main-process
 * IPC handler (repo invariant); this client-side `useProFeature` check is an
 * ADDITIONAL UX nicety so a Personal-mode user sees WHY it's disabled instead
 * of opening a dialog only to be blocked on confirm. The tooltip lives on a
 * wrapper `<span>` — Radix strips pointer events from disabled items
 * (see `TileDataExportMenuItems`).
 */
import { DropdownMenuItem, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { useProFeature } from '@/core/licensing';
import type { DashboardExportFlow } from './useDashboardExportFlow';

export function DashboardExportMenuItems({ flow }: { flow: DashboardExportFlow }) {
  const { canUse: canUseHtmlSnapshot } = useProFeature('dashboard-html-snapshot');
  const htmlWhy = canUseHtmlSnapshot ? undefined : 'Upgrade to Pro to export an interactive HTML snapshot';

  return (
    <>
      <DropdownMenuSeparator />
      <DropdownMenuItem onClick={flow.exportDefinition} data-testid="dashboard-export-definition-item">
        Export definition…
      </DropdownMenuItem>
      <DropdownMenuItem onClick={flow.exportPng} data-testid="dashboard-export-png-item">
        Export as PNG…
      </DropdownMenuItem>
      <DropdownMenuItem onClick={flow.exportPdf} data-testid="dashboard-export-pdf-item">
        Export as PDF…
      </DropdownMenuItem>
      <span title={htmlWhy}>
        <DropdownMenuItem
          disabled={!!htmlWhy}
          onClick={flow.exportHtmlSnapshot}
          data-testid="dashboard-export-html-item"
        >
          Export as HTML…
        </DropdownMenuItem>
      </span>
      <DropdownMenuItem onClick={flow.importDefinition} data-testid="dashboard-import-definition-item">
        Import definition…
      </DropdownMenuItem>
    </>
  );
}
